import { useAppContext } from "@/App";
import { Route, X, MapPin, Send, Trash2 } from "lucide-react";

export default function RouteBuilderPanel({
  routes, routeSites, setRouteSites, routeDriver, setRouteDriver,
  routeName, setRouteName, onCreateRoute, onDeleteRoute, onClose,
}) {
  const { hubData } = useAppContext();
  const { sites, drivers } = hubData;

  return (
    <div className="bg-slate-50/80 border-b border-amber-200 px-4 md:px-8 py-4 animate-slideInDown" data-testid="route-builder-panel">
      <div className="max-w-4xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest flex items-center gap-2">
            <Route className="w-4 h-4 text-amber-500" /> Route Planning
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900"><X className="w-4 h-4" /></button>
        </div>
        {/* Build New Route */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <input value={routeName} onChange={e => setRouteName(e.target.value)} placeholder="Route name (optional)"
            className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-400" data-testid="route-name-input" />
          <select value={routeDriver} onChange={e => setRouteDriver(e.target.value)} data-testid="route-driver-select"
            className="bg-white border border-slate-200 rounded-xl px-3 py-2.5 text-xs font-bold text-slate-400">
            <option value="">Select Driver</option>
            {drivers.map(d => <option key={d.driver_name} value={d.driver_name}>{d.driver_name}</option>)}
          </select>
          <button onClick={onCreateRoute} disabled={!routeDriver || routeSites.length === 0} data-testid="create-route-btn"
            className="bg-amber-500 hover:bg-amber-400 disabled:bg-slate-700 text-slate-950 rounded-xl px-3 py-2.5 text-xs font-black uppercase active:scale-95 transition-all flex items-center justify-center gap-2">
            <Send className="w-3.5 h-3.5" /> Assign Route ({routeSites.length} sites)
          </button>
        </div>
        {/* Site Selection */}
        <div className="flex flex-wrap gap-1.5 mb-4">
          {sites.map(s => {
            const isSelected = routeSites.includes(s.id);
            return (
              <button key={s.id} onClick={() => setRouteSites(prev => isSelected ? prev.filter(x => x !== s.id) : [...prev, s.id])}
                data-testid={`route-site-${s.id}`}
                className={`px-3 py-2 rounded-xl text-[10px] font-bold uppercase border transition-all ${
                  isSelected ? "bg-amber-50 border-amber-400 text-amber-400" : "bg-white border-slate-200 text-slate-400 hover:border-slate-300"
                }`}>
                <MapPin className="w-3 h-3 inline mr-1" />{s.name}
              </button>
            );
          })}
        </div>
        {/* Route History */}
        <div className="space-y-2 max-h-[200px] overflow-y-auto">
          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Route History</p>
          {routes.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-4">No routes yet</p>
          ) : routes.map(rt => (
            <div key={rt.id} className={`p-3 rounded-xl border flex items-center justify-between ${
              rt.status === "COMPLETED" ? "border-lime-500/20 bg-lime-500/5" : "border-slate-200 bg-white"
            }`} data-testid={`route-${rt.id}`}>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-800">{rt.name || "Unnamed Route"}</span>
                  <span className="text-[9px] font-bold bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded">{rt.driver_name}</span>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${rt.status === "COMPLETED" ? "bg-lime-50 text-lime-600" : "bg-amber-50 text-amber-400"}`}>{rt.status}</span>
                </div>
                <span className="text-[10px] text-slate-400">{rt.site_ids?.length || 0} sites - {new Date(rt.created_at).toLocaleDateString()}</span>
              </div>
              <div className="flex gap-1.5">
                <button onClick={() => { setRouteSites(rt.site_ids || []); setRouteDriver(rt.driver_name); setRouteName(rt.name || ""); }}
                  className="px-2.5 py-1.5 bg-slate-100 text-slate-400 rounded text-[10px] font-bold hover:text-slate-900" data-testid={`reuse-route-${rt.id}`}>Reuse</button>
                <button onClick={() => onDeleteRoute(rt.id)} className="p-1.5 text-slate-400 hover:text-red-400">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
